import Keycloak from "keycloak-js";
import { IUser } from "../api/services/auth/models";

export const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL || "https://id-dev.waffyapp.com",
  realm: process.env.REACT_APP_KEYCLOAK_REALM || "waffy",
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "portal-web",
});

// Must match one of the Valid Redirect URIs registered for the client in Keycloak.
const REDIRECT_URI =
  process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || `${window.location.origin}/`;

keycloak.onAuthSuccess = () => {
  if (process.env.NODE_ENV === "development") {
    console.log("[KC] onAuthSuccess");
  }
};
keycloak.onAuthError = (e) => {
  if (process.env.NODE_ENV === "development") {
    console.error("[KC] onAuthError", e);
  }
};
keycloak.onAuthLogout = () => {
  if (process.env.NODE_ENV === "development") {
    console.log("[KC] onAuthLogout");
  }
};
/**
 * Refreshes the token if it's within `minValiditySeconds` of expiring,
 * retrying once after a short delay before giving up. A lone failure here is
 * often transient (e.g. this fires right as a backgrounded tab regains
 * focus, before the network is actually ready) rather than a genuinely dead
 * session — every caller below (the proactive onTokenExpired/visibility
 * hooks and the reactive getAccessToken) shares this so a single blip never
 * forces an unnecessary re-login. Particularly important here: this realm's
 * accessTokenLifespan is only 10 minutes, so a customer's tab needs far less
 * idle time than an admin's to hit this path at all.
 */
const refreshToken = (minValiditySeconds: number): Promise<boolean> =>
  keycloak.updateToken(minValiditySeconds).catch(async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return keycloak.updateToken(minValiditySeconds);
  });

// Proactively refresh the token before it expires; redirect to login if refresh fails.
keycloak.onTokenExpired = () => {
  refreshToken(60).catch(() => keycloak.login());
};

// keycloak-js schedules onTokenExpired via a setTimeout computed relative to
// the token's exp claim — browsers throttle timers in backgrounded tabs, so
// a tab parked for a while can have that timer fire much later than the
// token's actual expiry (or the SSO session's own idle timeout may have
// already lapsed by then). Force an immediate check when the tab regains
// visibility to close that gap, rather than waiting on the throttled timer.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && keycloak.authenticated) {
    refreshToken(60).catch(() => keycloak.login());
  }
});

/** Call once at boot. Redirects to Keycloak if not authenticated. */
export const initOidc = (): Promise<boolean> => {
  if (process.env.NODE_ENV === "development") {
    console.log("[KC] initOidc called — URL:", window.location.href);
  }
  return keycloak.init({
    onLoad: "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false,
    redirectUri: REDIRECT_URI,
    enableLogging: process.env.NODE_ENV === "development",
  });
};

/** Synchronous token getter. May be up to ~30s stale; prefer getAccessToken for API calls. */
export const getAccessTokenSync = (): string | null =>
  keycloak.authenticated && keycloak.token ? keycloak.token : null;

/** Async token getter — refreshes if expiring within 30s, redirects to login on failure. */
export const getAccessToken = async (): Promise<string | null> => {
  if (!keycloak.authenticated) return null;
  try {
    await refreshToken(30);
  } catch {
    keycloak.login();
    return null;
  }
  return keycloak.token ?? null;
};

/**
 * Refreshes the token now, regardless of expiry, so it picks up roles granted since it was issued
 * (e.g. CLIENT_ADMIN right after organization self-registration).
 */
export const forceRefreshAccessToken = (): Promise<boolean> => refreshToken(-1);

/**
 * Builds an IUser from the Keycloak token claims.
 * Custom claims (userId, userIdentification, authorities, etc.) must be mapped
 * via Keycloak Protocol Mappers on the portal-web client.
 */
// Keycloak realm-role names → the authority strings the portal UI checks for.
// The portal gates everything behind CLIENT_ADMIN / ORGANIZATION_OWNER, but
// Keycloak emits the owner role as ORG_OWNER, so we add the alias.
const ROLE_ALIASES: Record<string, string[]> = {
  ORG_OWNER: ["ORGANIZATION_OWNER"],
};

/** Authorities the UI checks. Prefer an explicit `authorities` claim (if the BE
 *  adds the mapper later); otherwise derive them from the realm roles. */
const resolveAuthorities = (merged: Record<string, unknown>): string[] => {
  if (Array.isArray(merged.authorities)) return merged.authorities as string[];
  const realmAccess = merged.realm_access as { roles?: string[] } | undefined;
  const roles = realmAccess?.roles ?? [];
  return roles.flatMap((role) => [role, ...(ROLE_ALIASES[role] ?? [])]);
};

// Coerce numeric claims (userId, userIdentification arrive as numbers) to the
// strings the rest of the app compares against. Missing → "".
const asString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

export const getUserInfoFromToken = (): IUser | null => {
  const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
  const idParsed = keycloak.idTokenParsed as
    | Record<string, unknown>
    | undefined;
  if (!parsed) return null;
  const merged = { ...idParsed, ...parsed };
  return {
    id: asString(merged.userId ?? merged.sub),
    userIdentification: asString(merged.userIdentification),
    authorities: resolveAuthorities(merged),
    email: (merged.email ?? null) as string | null,
    username: (merged.preferred_username ?? null) as string | null,
    phoneNumber: (merged.phoneNumber ?? null) as string | null,
    brokerVerified: merged.brokerVerified as boolean | undefined,
    complexCreatorVerified: merged.complexCreatorVerified as
      | boolean
      | undefined,
    webView: merged.webView as boolean | undefined,
    profile: {
      fullName: (merged.name ??
        merged.fullName ??
        ([merged.given_name, merged.family_name].filter(Boolean).join(" ") ||
          undefined)) as string | undefined,
    },
  };
};

export const getUserId = (): string | null => {
  const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
  const idParsed = keycloak.idTokenParsed as
    | Record<string, unknown>
    | undefined;
  const id = idParsed?.userId ?? parsed?.userId;
  // userId arrives as a number from Keycloak — accept string or number.
  if (typeof id === "string") return id;
  if (typeof id === "number") return String(id);
  return null;
};

export const getRealmRoles = (): string[] => keycloak.realmAccess?.roles ?? [];

export const hasRealmRole = (role: string): boolean =>
  keycloak.hasRealmRole(role);

export const login = (): Promise<void> => keycloak.login();

/**
 * Starts the Waffy ID required action that gives a signed-in customer a portal login (password,
 * email) and registers the organization. It runs from the existing SSO session and returns here
 * with a new token that carries the organization claims.
 */
export const startPortalOnboarding = (): Promise<void> =>
  keycloak.login({
    action: "waffy-portal-onboarding",
    redirectUri: REDIRECT_URI,
  });

export const logout = (): Promise<void> =>
  keycloak.logout({
    redirectUri: window.location.origin,
    // idTokenHint lets KC verify which SSO session to end; without it KC may
    // silently ignore the end-session request and leave the Google SSO session alive.
    idTokenHint: keycloak.idToken,
  });
